/**
 * @file apiClient.ts
 * @description Typed Axios client with JWT authentication and error handling
 *
 * Task 18.3: Migrated from apiClient.js to TypeScript with:
 * - Explicit typing for all interceptors
 * - Timeout configuration (API_DEFAULT_TIMEOUT_MS)
 * - Dynamic import for logout to avoid circular dependency
 *
 * Reference: .taskmaster/docs/research/task-18-api-services-typescript-migration.md
 * Section 3.3
 */

import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import Cookies from 'js-cookie';

import { API_DEFAULT_TIMEOUT_MS } from '../types/api';

/**
 * Configured Axios instance with:
 * - Base URL from environment
 * - 30 second timeout
 * - JSON content type
 * - JWT auth interceptor
 * - 401 error handling
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: API_DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor: Inject JWT token into Authorization header
 *
 * Token is read from cookie first, then localStorage as fallback.
 * This supports both browser-based auth and SSR scenarios.
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = Cookies.get('jwt') || localStorage.getItem('jwt');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError): Promise<never> => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor: Handle 401 Unauthorized errors
 *
 * When a 401 is received, the user is logged out.
 * Dynamic import is used to avoid circular dependency with authService.
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  (error: AxiosError): Promise<never> => {
    if (error.response?.status === 401) {
      // Dynamic import to avoid circular dependency
      import('./authService').then(({ logout }) => {
        logout();
      });
    }
    return Promise.reject(error);
  }
);

export default apiClient;
